import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor, Check, Sparkles } from "lucide-react";
import { StripePortalLink } from "@/components/PaywallBanner";
import CancellationModal from "@/components/CancellationModal";

const TIER_RANK = { free: 0, monthly: 1, annual: 2, fleet: 2.5, lifetime: 3 };

export default function Pricing() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [params] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [billingNotice, setBillingNotice] = useState("");
    const [flags, setFlags] = useState({});
    const canceled = params.get("canceled") === "1";

    useEffect(() => {
        api.get("/flags").then(({ data }) => setFlags(data || {})).catch(() => {});
    }, []);

    useEffect(() => {
        // Try /billing/status first (authed users get the rich payload).
        // Fall back to /billing/lifetime-remaining for anonymous visitors.
        api.get("/billing/status")
            .then(({ data }) => setStatus(data))
            .catch(() => {
                api.get("/billing/lifetime-remaining")
                    .then(({ data }) => setStatus({ plan_tier: "free", lifetime_remaining: data.remaining, lifetime_cap: data.cap }))
                    .catch(() => {});
            });
    }, []);

    const subscribe = async (plan) => {
        setErr("");
        setLoadingPlan(plan);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setLoadingPlan(null);
        }
    };

    const annualFirst = Boolean(flags.annual_first_pricing);
    let tiers = [
        {
            key: "free",
            label: "Free",
            price: "$0",
            unit: "forever",
            sub: "The captain's logbook",
            highlight: false,
            features: [
                "1 vessel, unlimited entries",
                "Maintenance, fuel, costs & cruising log",
                "Photos & receipts on any entry",
                "Cruiser Contacts — boats you've met",
                "7-day Pro trial included at signup",
            ],
        },
        {
            key: "monthly",
            label: "Monthly",
            price: "$4.99",
            unit: "USD / month",
            sub: "Everything, month to month",
            highlight: false,
            features: [
                "Unlimited vessels",
                "Co-captains — share with crew",
                "AI receipt scan, survey & parts import",
                "Charts & fuel-economy analytics",
                "Checklists, custom fields, SMS alerts, PDF export",
                "Cancel anytime",
            ],
        },
        {
            key: "annual",
            label: "Annual",
            price: "$49.99",
            unit: "USD / year",
            sub: "Save ~17% — most popular",
            highlight: true,
            features: [
                "Everything in Monthly",
                "17% savings vs monthly ($59.88 → $49.99)",
                "Priority support",
                "Annual maintenance summary export",
                "Cancel anytime",
            ],
        },
        {
            key: "lifetime",
            label: "Lifetime",
            price: "$150",
            unit: "USD · one-time",
            sub: "Founder pricing · limited to 150",
            highlight: false,
            features: [
                "Everything in Annual — forever",
                "All future features included",
                "Founder badge on your profile",
                "Never pay again",
                "Only 150 seats, ever",
            ],
        },
    ];
    // Release switches (Oct 2026): annual-first ordering + Fleet tier probe.
    if (annualFirst) {
        const order = { annual: 0, monthly: 1, free: 2, lifetime: 3 };
        tiers = [...tiers].sort((a, b) => (order[a.key] ?? 9) - (order[b.key] ?? 9));
        const annualTier = tiers.find((t) => t.key === "annual");
        if (annualTier) annualTier.unit = "USD / year · ≈ $4.17/mo";
    }
    if (flags.fleet_tier) {
        tiers.push({
            key: "fleet",
            label: "Fleet",
            price: "$24.99",
            unit: "USD / month",
            sub: "Charter operators & lodges",
            highlight: false,
            features: [
                "For charter operators & lodges",
                "3+ vessels & full crew access",
                "Everything in Pro",
                "Priority support",
                "Cancel anytime",
            ],
        });
    }

    const isLifetime = user?.subscription_status === "lifetime";
    // Freemium: is_active is always true (write access). "Paying" = an active
    // recurring subscription that can be managed/canceled via Stripe.
    const isPaying = status?.subscription_status === "active";
    const isProTier = status?.plan_tier === "pro";

    return (
        <div data-testid="pricing-page" style={{ minHeight: "100vh" }}>
            {/* Header */}
            <header style={{ maxWidth: 1200, margin: "0 auto", padding: "22px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Link to={user ? "/app" : "/"} data-testid="pricing-brand-link" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
                    <Anchor size={20} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 24 }}>Bosun's Mate</span>
                </Link>
                {user ? (
                    <button onClick={() => nav("/app")} className="bm-btn bm-btn-ghost" data-testid="pricing-back-btn">Back to app</button>
                ) : (
                    <button onClick={() => nav("/login")} className="bm-btn bm-btn-ghost">Sign in</button>
                )}
            </header>

            {/* Hero */}
            <section style={{ maxWidth: 900, margin: "0 auto", padding: "30px 32px 30px", textAlign: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, border: "1px solid var(--bm-border)", color: "var(--bm-text-muted)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 18 }}>
                    <Sparkles size={12} color="var(--bm-gold)" /> Captain-friendly pricing
                </div>
                <h1 className="bm-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.05, margin: 0, fontWeight: 500 }}>
                    Start free. Go Pro when your boat demands more.
                </h1>
                <p style={{ color: "var(--bm-text-muted)", margin: "16px auto 0", maxWidth: 560, fontSize: 15, lineHeight: 1.6 }}>
                    Every new account starts with 7 days of Pro — no card needed. The core logbook stays free forever.
                    Pro unlocks unlimited vessels, co-captains, AI extraction, charts and more.
                    Or grab a founder lifetime seat before all 150 are gone.
                </p>
            </section>

            {canceled && (
                <div data-testid="pricing-canceled-banner" className="bm-card" style={{ maxWidth: 600, margin: "0 auto 18px", padding: 14, textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13 }}>
                    Checkout canceled — no charge made. Pick a plan when you're ready.
                </div>
            )}

            {err && (
                <div data-testid="pricing-error" style={{ maxWidth: 600, margin: "0 auto 16px", padding: 12, color: "var(--bm-bad)", textAlign: "center", fontSize: 14 }}>{err}</div>
            )}

            {isLifetime && (
                <div className="bm-card" style={{ maxWidth: 720, margin: "0 auto 24px", padding: 20, textAlign: "center" }}>
                    <div className="bm-display" style={{ fontSize: 22, color: "var(--bm-gold)" }}>You're a founder.</div>
                    <div style={{ color: "var(--bm-text-muted)", fontSize: 14, marginTop: 4 }}>You own Bosun's Mate for life. No further payment needed. ⚓</div>
                </div>
            )}

            {/* Plans */}
            <section style={{ maxWidth: 1280, margin: "0 auto", padding: "10px 32px 60px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 18 }}>
                    {tiers.map((t) => {
                        const lifetimeRemain = status?.lifetime_remaining ?? null;
                        const lifetimeSoldOut = t.key === "lifetime" && lifetimeRemain === 0;
                        const currentPlanKey = status?.subscription_plan;
                        const isFreeTierCard = t.key === "free";
                        const isCurrent = isFreeTierCard
                            ? Boolean(user && !isProTier)
                            : currentPlanKey === t.key && (isPaying || isLifetime);
                        // Determine label vs current plan (recurring plans only)
                        const currentRank = currentPlanKey ? TIER_RANK[currentPlanKey] || 0 : 0;
                        const thisRank = TIER_RANK[t.key] || 0;
                        const isUpgrade = isPaying && currentRank > 0 && thisRank > currentRank;
                        const isDowngrade = isPaying && currentRank > 0 && thisRank < currentRank && !isFreeTierCard;
                        return (
                            <div
                                key={t.key}
                                data-testid={`tier-${t.key}`}
                                className="bm-card"
                                style={{
                                    padding: "26px 24px 24px",
                                    position: "relative",
                                    border: t.highlight ? "1px solid var(--bm-gold)" : undefined,
                                    boxShadow: t.highlight ? "0 12px 40px -20px rgba(212,181,114,0.4)" : undefined,
                                }}
                            >
                                {t.highlight && (
                                    <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: "var(--bm-gold)", color: "#1a1304", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 999 }}>
                                        Best value
                                    </div>
                                )}
                                <div className="bm-display" style={{ fontSize: 22, color: "var(--bm-gold)", marginBottom: 4 }}>{t.label}</div>
                                <div style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>{t.sub}</div>
                                <div style={{ marginTop: 18, marginBottom: 18 }}>
                                    <span className="bm-display" style={{ fontSize: 48, fontWeight: 500, color: "var(--bm-text)" }}>{t.price}</span>
                                    <span style={{ color: "var(--bm-text-muted)", fontSize: 14, marginLeft: 6 }}>{t.unit}</span>
                                </div>
                                {t.key === "lifetime" && lifetimeRemain !== null && (
                                    <div data-testid="lifetime-remaining" style={{ fontSize: 12, color: lifetimeRemain <= 20 ? "var(--bm-warn)" : "var(--bm-text-muted)", marginBottom: 14 }}>
                                        {lifetimeSoldOut ? "Sold out — try monthly or annual" : `${lifetimeRemain} of 150 founder seats remaining`}
                                    </div>
                                )}
                                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                                    {t.features.map((f) => (
                                        <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, color: "var(--bm-text)" }}>
                                            <Check size={15} color="var(--bm-blue)" style={{ flexShrink: 0, marginTop: 2 }} /> <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    data-testid={`subscribe-${t.key}-btn`}
                                    disabled={isFreeTierCard ? Boolean(user) : (loadingPlan === t.key || lifetimeSoldOut || isCurrent || isLifetime || isDowngrade)}
                                    onClick={() => (isFreeTierCard ? nav("/register") : subscribe(t.key))}
                                    className={t.highlight ? "bm-btn bm-btn-gold" : "bm-btn bm-btn-primary"}
                                    style={{ width: "100%", padding: "12px 18px", opacity: loadingPlan === t.key ? 0.6 : 1 }}
                                >
                                    {isFreeTierCard
                                        ? (isCurrent ? "Current plan" : user ? "Included with Pro" : "Start free")
                                        : isCurrent ? "Current plan"
                                        : lifetimeSoldOut ? "Sold out"
                                        : isLifetime ? "Already owned"
                                        : isDowngrade ? "Downgrade not supported"
                                        : loadingPlan === t.key ? "Boarding…"
                                        : isUpgrade ? `Upgrade to ${t.label}`
                                        : t.key === "lifetime" ? "Buy lifetime"
                                        : t.key === "fleet" ? "Get Fleet"
                                        : "Get Pro"}
                                </button>
                                {isUpgrade && (
                                    <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 8, textAlign: "center" }}>
                                        Your current {currentPlanKey} subscription will be canceled automatically after this charge.
                                    </div>
                                )}
                                {isDowngrade && (
                                    <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 8, textAlign: "center" }}>
                                        To downgrade, cancel your current plan first and re-subscribe after it ends.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Reassure captains: closing the paywall means "keep your free
                plan," not "lose your data." Sits right below the plan grid
                so eyes catch it after scanning prices. Only shown when the
                captain isn't already paying — a paying captain seeing "Stay
                on free" makes no sense. */}
            {!isPaying && !isLifetime && (
                <section style={{ maxWidth: 880, margin: "20px auto 0", padding: "0 32px", textAlign: "center" }}>
                    <button
                        type="button"
                        data-testid="pricing-stay-free-btn"
                        onClick={() => nav(user ? "/app" : "/")}
                        className="bm-btn bm-btn-ghost"
                        style={{
                            padding: "10px 22px", fontSize: 13,
                            borderRadius: 999,
                            display: "inline-flex", alignItems: "center", gap: 8,
                        }}
                    >
                        <Anchor size={14} />
                        Stay on Free Plan — 1 Vessel, Unlimited Entries
                    </button>
                    <p style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 8, marginBottom: 0 }}>
                        Free forever. Your data and access to core logging stay yours.
                    </p>
                </section>
            )}

            <section style={{ maxWidth: 880, margin: "16px auto 0", padding: "0 32px" }}>
                <p style={{ textAlign: "center", color: "var(--bm-text-dim)", fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                    By subscribing you agree to our{" "}
                    <Link data-testid="pricing-terms-link" className="bm-link" to="/terms">Terms of Service</Link>
                    {" "}and{" "}
                    <Link data-testid="pricing-privacy-link" className="bm-link" to="/privacy">Privacy Policy</Link>.
                    Subscriptions auto-renew until cancelled — manage anytime from the customer portal.
                </p>
            </section>

            <footer style={{ borderTop: "1px solid var(--bm-border-soft)", padding: "24px 32px", marginTop: 24, textAlign: "center", color: "var(--bm-text-dim)", fontSize: 13 }}>
                {isPaying && !isLifetime && (
                    <div style={{ marginBottom: 16, display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                        <StripePortalLink label="Update payment method" />
                        <span style={{ color: "var(--bm-text-dim)" }}>·</span>
                        <button
                            type="button"
                            data-testid="pricing-cancel-btn"
                            onClick={() => setShowCancelModal(true)}
                            className="bm-link"
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
                        >
                            Cancel subscription →
                        </button>
                    </div>
                )}
                {billingNotice && (
                    <div data-testid="pricing-billing-notice" style={{ marginBottom: 12, color: "var(--bm-good)" }}>
                        {billingNotice}
                    </div>
                )}
                <Link to={user ? "/app" : "/"} className="bm-link">Back to {user ? "the helm" : "the home page"}</Link>
                &nbsp;·&nbsp;
                <Link to="/terms" className="bm-link">Terms</Link>
                &nbsp;·&nbsp;
                <Link to="/privacy" className="bm-link">Privacy</Link>
                &nbsp;·&nbsp;
                Questions? <a href="mailto:captain@bosunsmate.app" className="bm-link">captain@bosunsmate.app</a>
            </footer>
            <CancellationModal
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onStayed={(kind) => {
                    const map = {
                        pause:    "Your subscription is paused. Resume anytime.",
                        discount: "50% off applied to your next three billing cycles.",
                        gift:     "Your next billing cycle is free — enjoy!",
                    };
                    setBillingNotice(map[kind] || "Thanks for sticking around.");
                    setTimeout(() => setShowCancelModal(false), 2400);
                }}
                onCanceled={() => {
                    setBillingNotice("Subscription will end at the close of the current period.");
                    setTimeout(() => setShowCancelModal(false), 2400);
                }}
            />
        </div>
    );
}
